<script lang="ts">
  import {slide} from 'svelte/transition';
  import {sineInOut} from 'svelte/easing';

  import {deal} from 'Store/InvestorFlowStore';
  import {openLinksInNewTab} from 'Utility/formatters/replaceAnchorTags';
  import AngleDown from 'Components/Common/icons/AngleDown.svelte';

  let isOpen: boolean = false;
  let showDetails: boolean = false;
  let invitationContent: HTMLElement;
  let showMore: boolean = true;
  let showContentButton: boolean = false;

  const toggle = (): void => {
    isOpen = !isOpen;
    showMore = !showMore;
  };

  const toggleShowDetails = (): void => {
    showDetails = !showDetails;
  };

  $: {
    if (invitationContent) {
      if (invitationContent.clientHeight >= 208 && window.innerWidth > 1025) {
        showContentButton = true;
      } else {
        showMore = false;
      }
    }
  }

  $: pageContentBody = openLinksInNewTab($deal.page_content?.body);
</script>

<div class="invitation-content">
  <div class="content-title-container">
    {#if pageContentBody}
      <p class="content-title" data-testid="content-title">
        {$deal.title_content ? $deal.title_content : 'Additional Information'}
      </p>
      <button
        type="button"
        class="btn btn-outline show-details-button"
        data-testid="show-details-button"
        on:click={() => toggleShowDetails()}
        aria-expanded={showDetails}
      >
        {!showDetails ? 'Show' : 'Hide'} details
        <AngleDown />
      </button>
    {/if}
  </div>
  {#if showDetails}
    <div
      class="invitation-content-mobile"
      data-testid="invitation-content-mobile"
      transition:slide={{duration: 200, easing: sineInOut}}
    >
      <p class="content-body">
        {@html pageContentBody}
      </p>
    </div>
  {/if}
  {#if pageContentBody}
    <div class="invitation-content-desktop">
      <p
        class="content-body"
        class:blur-content={showMore}
        data-testid="content-body-desktop"
        bind:this={invitationContent}
      >
        {@html pageContentBody}
      </p>
      <button
        type="button"
        class="btn btn-outline show-more-button"
        class:show={showContentButton}
        on:click={toggle}
        aria-expanded={isOpen}
      >
        {showMore ? 'Show more' : 'Show less'}
        <AngleDown />
      </button>
    </div>
  {/if}
</div>

<style lang="postcss">
  .invitation-content {
    margin-top: 0.75rem;
    color: var(--palettes-neutral-575, #667085);
  }
  .content-title-container {
    display: flex;
    justify-content: space-between;
    align-items: baseline;

    @media screen and (min-width: 768px) {
      margin-bottom: 0.75rem;
    }
  }

  .content-title {
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 500;
    margin-bottom: 0;

    @media screen and (min-width: 768px) {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
  }

  .content-body {
    display: block;
    font-size: 1rem;
    line-height: 1.25rem;

    &.blur-content {
      display: block;
      position: relative;
      max-height: 208px;
      overflow: hidden;

      &::after {
        content: '';
        position: absolute;
        z-index: 1;
        bottom: 0;
        left: 0;
        background-image: linear-gradient(
          to bottom,
          rgba(255, 255, 255, 0),
          var(--palettes-neutral-0, rgba(255, 255, 255, 1)) 90%
        );
        width: 100%;
        height: 4em;
      }
    }
  }

  .show-details-button {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--palettes-info-500, #1570ef);

    &:hover {
      text-decoration: underline;
    }

    &:not(:disabled):active,
    &:focus {
      box-shadow: none;
    }

    & :global(.angle-down) {
      transition: transform 0.2s ease-in;
      margin-left: 0.625rem;

      & :global(path) {
        stroke: var(--palettes-info-500, #1570ef);
      }
    }

    &[aria-expanded='true'] :global(.angle-down) {
      transform: rotate(0.5turn);
    }

    @media screen and (min-width: 768px) {
      display: none;
    }
  }

  .show-more-button {
    color: var(--palettes-neutral-575, #667085);
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.25rem;
    margin-top: 1rem;
    margin: auto;
    display: none;

    & :global(.angle-down) {
      transition: transform 0.2s ease-in;
      margin-left: 0.815rem;
    }

    &[aria-expanded='true'] :global(.angle-down) {
      transform: rotate(0.5turn);
    }

    &.show {
      display: block;
    }
  }

  .invitation-content-desktop {
    display: none;

    @media screen and (min-width: 768px) {
      display: block;
    }

    & button {
      &:not(.disabled):active,
      &:focus {
        box-shadow: none;
      }
    }
  }

  .invitation-content-mobile {
    display: block;

    @media screen and (min-width: 768px) {
      display: none;
    }
  }
</style>
